/**
 * PLT-7087 — where a product set card stands among the cards after it in its thread. Pure, and kept
 * apart from `use-product-set-card-payload.ts` so it can be tested without the chat runtime.
 *
 * PLT-6999 — the same for the site review that follows a custom company's products when its sites
 * were identified (`siteReviewStanding`).
 *
 * PLT-7456 / PLT-7453 — and what an approved card's approval recorded (`approvingPublishResult`).
 */
import { type CompanyGeolocationSiteValues } from '@upright/common/model/input/companies/geolocation';

export const PRODUCT_SET_CARD_APP_NAME = 'product-set-card';
const PUBLISH_TOOL_NAME = 'publish_dd_product_set';
const SITE_REVIEW_TOOL_NAME = 'propose_site_locations';

/**
 * - `replaced`: a newer card of the same kind for the same company follows it.
 * - `approved`: the company's approval follows it.
 * - `current`: neither.
 */
export type ProductSetCardStanding = 'current' | 'replaced' | 'approved';

interface ThreadMessageLike {
  readonly content: readonly unknown[];
}

interface ToolCallPartLike {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  result?: { data?: unknown; _ui?: { appName?: string } };
}

function isToolCallPart(part: unknown): part is ToolCallPartLike {
  return typeof part === 'object' && part !== null && (part as { type?: unknown }).type === 'tool-call';
}

export function companyIdOf(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const companyId = (data as { companyId?: unknown }).companyId;
  return typeof companyId === 'string' ? companyId : undefined;
}

/** The tool calls for `companyId` after the one with `toolCallId`, in thread order. */
function laterCallsForCompany(
  messages: readonly ThreadMessageLike[],
  toolCallId: string,
  companyId: string,
): ToolCallPartLike[] {
  const later: ToolCallPartLike[] = [];
  let afterCard = false;
  for (const message of messages) {
    for (const part of message.content) {
      if (!isToolCallPart(part)) continue;
      if (!afterCard) {
        afterCard = part.toolCallId === toolCallId;
        continue;
      }
      if (companyIdOf(part.result?.data) === companyId) later.push(part);
    }
  }
  return later;
}

/** Only results that drew a card count (`_ui.appName` set): a refused publish draws none. */
function isPublishCard(part: ToolCallPartLike): boolean {
  return part.toolName === PUBLISH_TOOL_NAME && !!part.result?._ui?.appName;
}

/**
 * A site review the user reached by approving a custom company's products (PLT-6999): a site card
 * for a company awaiting its first approval that the agent marked as the review (PLT-7302), or one
 * proposing no sites of its own (older stored results carry no mark). A proposal made while the
 * products still await approval approves nothing.
 */
export function isFirstApprovalSiteReview(part: Pick<ToolCallPartLike, 'toolName' | 'result'>): boolean {
  const data = part.result?.data;
  if (part.toolName !== SITE_REVIEW_TOOL_NAME || typeof data !== 'object' || data === null) return false;
  const { firstApproval, review, sites } = data as { firstApproval?: unknown; review?: unknown; sites?: unknown };
  return firstApproval === true && (review === true || (Array.isArray(sites) && sites.length === 0));
}

/**
 * A product set card is replaced by a later product set card for the company, and approved by a later
 * publish card or by the site review its approval opens. Only results that drew a card count: a
 * refused publish or a still-generating read draws none, so it replaces or approves nothing. A card
 * not found in the thread is current.
 */
export function productSetCardStanding(
  messages: readonly ThreadMessageLike[],
  toolCallId: string,
  companyId: string,
): ProductSetCardStanding {
  let approved = false;
  for (const part of laterCallsForCompany(messages, toolCallId, companyId)) {
    if (part.result?._ui?.appName === PRODUCT_SET_CARD_APP_NAME) return 'replaced';
    if (isPublishCard(part) || isFirstApprovalSiteReview(part)) approved = true;
  }
  return approved ? 'approved' : 'current';
}

/**
 * Whether a first-approval site review for the company comes before the card with `toolCallId`, so a
 * proposal saved after it changed the set that review was opened on (PLT-7368).
 */
export function siteReviewOpenedBefore(
  messages: readonly ThreadMessageLike[],
  toolCallId: string,
  companyId: string,
): boolean {
  for (const message of messages) {
    for (const part of message.content) {
      if (!isToolCallPart(part)) continue;
      if (part.toolCallId === toolCallId) return false;
      if (companyIdOf(part.result?.data) === companyId && isFirstApprovalSiteReview(part)) return true;
    }
  }
  return false;
}

/**
 * A first-approval site review is replaced by a later one for the company — the products were
 * approved again, or the user asked for a change while it was open — and approved by a later
 * publish card, which its own approval leads to.
 */
export function siteReviewStanding(
  messages: readonly ThreadMessageLike[],
  toolCallId: string,
  companyId: string,
): ProductSetCardStanding {
  let approved = false;
  for (const part of laterCallsForCompany(messages, toolCallId, companyId)) {
    if (isFirstApprovalSiteReview(part)) return 'replaced';
    if (isPublishCard(part)) approved = true;
  }
  return approved ? 'approved' : 'current';
}

/**
 * The result of the publish an approved card's standing comes from: the first publish card for the
 * company after the card with `toolCallId`. Undefined when none follows, as for a product set card
 * approved by the site review it opened while that review's publish is still to come.
 *
 * It is the stored object itself, so a `useThread` selector returning it keeps its identity.
 */
export function approvingPublishResult(
  messages: readonly ThreadMessageLike[],
  toolCallId: string,
  companyId: string,
): unknown {
  return laterCallsForCompany(messages, toolCallId, companyId).find(isPublishCard)?.result?.data;
}

/**
 * The products a publish result records as approved (PLT-7456), or undefined for a result from before
 * publishes recorded them.
 */
export function approvedProductsOf(publishResult: unknown): readonly unknown[] | undefined {
  if (typeof publishResult !== 'object' || publishResult === null) return undefined;
  const record = (publishResult as { approvedProductSet?: { products?: unknown } | null }).approvedProductSet;
  return Array.isArray(record?.products) ? record.products : undefined;
}

/**
 * The sites a publish result records as approved (PLT-7453), or undefined when it records none: a result
 * from before publishes recorded them, or for a company whose sites are not assessed.
 */
export function approvedSitesOf(publishResult: unknown): CompanyGeolocationSiteValues[] | undefined {
  if (typeof publishResult !== 'object' || publishResult === null) return undefined;
  const sites = (publishResult as { approvedSites?: unknown }).approvedSites;
  return Array.isArray(sites) ? (sites as CompanyGeolocationSiteValues[]) : undefined;
}
